import React, { useEffect, useCallback, useContext, useMemo } from 'react';

import {
    BudgetProposalT,
    BudgetProposalSettingsT,
    CellRendererParams,
    FieldSettingsT,
} from 'routes/BudgetModule/config/types';

import {
    BUDGET_LINE_ID,
    CAN_ADD_INSCRIPTION,
    SPLIT_BY,
} from 'routes/BudgetModule/config/constants';
import { isActiveBudgetProposal } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import 'routes/BudgetModule/components/BudgetProposal/shared/Grids/_commonGrid.scss';
import { capitalize, debounce } from 'lodash';
import { translateBlockValue } from 'i18n';
import { BudgetExpenseOrIncome, STALE_TIME } from 'business/constants';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import NumberFloatingFilter from 'ui/AgGridReact/NumberFloatingFilter';
import {
    getImputationDimensionsLabelsAndCodes,
    EXCLUDED_DIMENSIONS_BY_CONTEXT,
} from 'routes/BudgetModule/config/hooks';
import PercentRealizedCellRenderer from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/components/PercentRealizedCellRenderer';
import AggStatusCellRenderer from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/components/AggStatusCellRenderer';
import DetailCellRenderer from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/components/DetailCellRenderer';
import { sideBar } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/gridHelpers/gridHelpers';
import AgContainer from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/components/AgContainer';
import { ColDef, PaginationChangedEvent } from 'ag-grid-community';

import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';
import {
    getColumnSettings,
    onColumnMoved,
    onColumnResized,
    onColumnRowGroupChanged,
    onColumnVisible,
    onRowClicked,
} from '../helpers/gridHelpers';
import { useFullScreen } from 'contexts/FullScreenContext';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { EditableValuesByResource } from 'routes/BudgetModule/helpers/hooks/writeScopes';
import { ExpenseIncomeImputationDataT } from '../../hooks/expenseIncomeImputations';
import { OnGroupTracking } from 'helpers/trackerHelpers';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';

type Props = {
    budgetProposal: BudgetProposalT;
    budgetProposals: BudgetProposalT[];
    expenseOrIncome: BudgetExpenseOrIncome;
    imputationsData: ExpenseIncomeImputationDataT[];
    inscriptions: InscriptionT[];
    columns: ColDef[];
    editableValuesByResource: EditableValuesByResource;
    filterInscriptions: string;
    onGridReady?: (params: FlowAnyObject) => void | null;
    settings: BudgetProposalSettingsT | undefined;
    onSettingsEdit: (arg1: Partial<BudgetProposalSettingsT>) => void;
    columnsSettings: FieldSettingsT[];
    setColumnsSettings: (columnSettings: FieldSettingsT[]) => void;
    // tracking props
    trackGrouping: OnGroupTracking;
    onFilterChanged?: (params: FlowAnyObject) => void;
    onSortChanged?: (params: FlowAnyObject) => void;
    onRowGroupOpened?: (params: FlowAnyObject) => void;
    onPaginationChanged?: (event: PaginationChangedEvent) => void;
    inscriptionsQueryCacheKey: InscriptionQueryKey;
};

const DEBOUNCE_TIME = 2000;

function ImputationsGrid({
    budgetProposal,
    budgetProposals,
    expenseOrIncome,
    imputationsData,
    inscriptions,
    columns,
    editableValuesByResource,
    filterInscriptions,
    onGridReady = () => {},
    settings,
    onSettingsEdit,
    columnsSettings,
    setColumnsSettings,
    trackGrouping,
    onFilterChanged = () => {},
    onSortChanged = () => {},
    onRowGroupOpened = () => {},
    onPaginationChanged,
    inscriptionsQueryCacheKey,
}: Props): React.ReactElement {
    const { isFullScreen } = useFullScreen();
    const { projects } = useGetProjects({
        staleTime: STALE_TIME, // no need to re-fetch it each time we expand a row
        enabled: isActiveBudgetProposal(budgetProposal), // no need to fetch if no inscription displayed
    });

    const imputationRawDimensions = useContext(ImputationDimensionsContext);
    const imputationDimensions = getImputationDimensionsLabelsAndCodes(
        imputationRawDimensions,
        [
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.BudgetProposal,
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.ExpenseIncome,
        ],
    );

    useEffect(() => {
        // Race condition: when call to fetch settings is finished after this component is rendered,
        // useState(settings.expenseGrid.columns) is called when settings still has its initial state
        if (columnsSettings.length === 0) {
            setColumnsSettings(settings?.expenseGrid.columns ?? []);
        }
    }, [settings, imputationDimensions]);

    // we use a useCallback to make sure the debounced function stays the same,
    // if it is redefined, the debounce should not work
    const mutateSettings = useCallback(
        debounce(onSettingsEdit, DEBOUNCE_TIME),
        [onSettingsEdit],
    );
    // We sync the local state and then update the backend set
    const updateSettings = (colDefs: ColDef[]) => {
        const newSettings: FieldSettingsT[] = colDefs.map((colDef, index) => ({
            field: colDef.field || '',
            hidden: !!colDef.hide,
            index,
            grouped: !!colDef.rowGroup,
            width: colDef.width === undefined ? 100 : colDef.width,
            groupIndex:
                colDef.rowGroupIndex === undefined
                    ? null
                    : colDef.rowGroupIndex,
        }));

        setColumnsSettings(newSettings);

        mutateSettings({
            expenseGrid: {
                columns: newSettings,
            },
        });
    };

    const colDefs: ColDef[] = useMemo(() => {
        const colDefsInUseMemo = columns.map((column) => {
            const columnSettings = getColumnSettings(column, columnsSettings);
            return {
                headerName: capitalize(
                    translateBlockValue(
                        column.field?.split(SPLIT_BY)[0] || '',
                        true,
                    ),
                ),
                ...column,
                resizable: true,
                field: column.field,
                suppressFloatingFilterButton: true,
                menuTabs: [
                    'filterMenuTab' as const,
                    'generalMenuTab' as const,
                    'columnsMenuTab' as const,
                ],
                floatingFilter: true,
                hide:
                    (columnSettings && columnSettings.hidden) ||
                    (!columnSettings && column.hide),
                width: columnSettings?.width,
                rowGroup:
                    columnSettings &&
                    typeof columnSettings.grouped === 'boolean'
                        ? columnSettings.grouped
                        : false,
                rowGroupIndex: columnSettings && columnSettings.groupIndex,
            };
        });
        const isAtLeastOneGroupingDone = colDefsInUseMemo.some(
            (col) => col.rowGroup,
        );
        // if at least one grouping is done, then the expandButtonColDef is hidden
        return colDefsInUseMemo.map((colDef) => ({
            ...colDef,
            hide:
                colDef.field === `${BUDGET_LINE_ID}..${budgetProposal.exercice}`
                    ? isAtLeastOneGroupingDone
                    : colDef.hide,
        }));
    }, [JSON.stringify(columns), columnsSettings, budgetProposal.exercice]);

    // WARNING: may break notification redirection if removed
    // we want to re-render only when data value has changed, not reference
    const memoizedImputationData = useMemo(
        () => imputationsData,
        [JSON.stringify(imputationsData)],
    );

    // -------- Edition of inscription depending on user Rights and phase --------
    const showInscriptions = isActiveBudgetProposal(budgetProposal);

    // necessary to stabilize ref and avoid unmounting the detailcellrenderer when updating its comment or amount
    const detailCellRendererParams = useCallback(
        ({
            data: detailData,
            context: detailContext,
        }: CellRendererParams<ResponseDataT>) => {
            const budgetLineId =
                detailData?.[`${BUDGET_LINE_ID}..${budgetProposal.exercice}`];
            const canAddInscription =
                detailData?.[CAN_ADD_INSCRIPTION] ?? false;
            return {
                budgetProposal,
                budgetProposals,
                budgetLineId,
                canAddInscription,
                editableValuesByResource,
                expenseOrIncome,
                filterInscriptions,
                projects: detailContext.projects,
                queryCacheKey: inscriptionsQueryCacheKey,
            };
        },
        [
            budgetProposal,
            budgetProposals,
            editableValuesByResource,
            expenseOrIncome,
            filterInscriptions,
            inscriptionsQueryCacheKey,
        ],
    );
    return (
        <AgContainer className="ag-theme-material">
            <AgGridReact
                masterDetail={showInscriptions}
                embedFullWidthRows
                autoGroupColumnDef={{
                    minWidth: 100,
                    width: 350,
                    resizable: true,
                    headerName: undefined,
                    lockPosition: true,
                }}
                rowHeight={36}
                rowGroupPanelShow="always"
                suppressAnimationFrame
                rowData={memoizedImputationData}
                components={{
                    detailCellRenderer: DetailCellRenderer,
                    aggStatusCellRenderer: AggStatusCellRenderer,
                    percentRealizedCellRenderer: PercentRealizedCellRenderer,
                    textFloatingFilter: TextFloatingFilter,
                    numberFloatingFilter: NumberFloatingFilter,
                }}
                sideBar={sideBar}
                pagination={!isFullScreen}
                paginationAutoPageSize={!isFullScreen}
                context={{ inscriptions, projects }}
                detailCellRenderer="detailCellRenderer"
                detailRowAutoHeight
                detailCellRendererParams={detailCellRendererParams}
                onRowClicked={onRowClicked}
                onGridReady={onGridReady}
                onColumnVisible={onColumnVisible(updateSettings)}
                onSortChanged={onSortChanged}
                onFilterChanged={onFilterChanged}
                onColumnResized={onColumnResized(updateSettings)}
                onColumnRowGroupChanged={onColumnRowGroupChanged(
                    updateSettings,
                    trackGrouping,
                )}
                columnDefs={colDefs}
                onRowGroupOpened={onRowGroupOpened}
                onColumnMoved={onColumnMoved(updateSettings)}
                tooltipShowDelay={500}
                onPaginationChanged={onPaginationChanged}
            />
        </AgContainer>
    );
}

export default ImputationsGrid;
